import { DataResult, State } from "@progress/kendo-data-query";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DerivativesQuotesGrid, InsuranceQuotesGrid } from ".";
import { objectToLookup } from "../../utilities/objectUtilities";
import { ICommodity } from "../quotes";
import { AccumulatorQuotesGrid } from "./AccumulatorQuotesGrid";

interface QuotesGridProps {
  quotes: any[];
  businessTypes: any;
  commodities: ICommodity[];
  onDataChange: (result: DataResult | undefined, state?: State) => void;
  onRefresh: () => void;
  selectedTab?: string | null;
}

export const QuotesGrid = (props: QuotesGridProps) => {
  const { quotes, businessTypes, selectedTab, ...others } = props;
  const listItems = objectToLookup(businessTypes || []);

  // Sort business types in desired order: Derivatives, Insurance, Accumulators
  const sortedListItems = [...listItems].sort((a, b) => {
    const order = { derivatives: 1, insurance: 2, accumulator: 3, accumulators: 3 };
    const aOrder = order[a.label.toLowerCase()] || 999;
    const bOrder = order[b.label.toLowerCase()] || 999;
    return aOrder - bOrder;
  });

  // Calculate initial selected tab based on selectedTab parameter
  const [selected, setSelected] = useState(0);

  // Update selected tab when selectedTab prop changes
  useEffect(() => {
    if (!selectedTab) {
      setSelected(0);
      return;
    }
    const tabLower = selectedTab.toLowerCase();
    const index = sortedListItems.findIndex((item) => item.label.toLowerCase() === tabLower);
    if (index >= 0) {
      setSelected(index);
    }
  }, [selectedTab, sortedListItems]);

  // Keep URL search param in sync when user clicks tabs
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = sortedListItems.map((item) => {
    const busQuotes = quotes.filter((q) => q.businessTypeId === item.value);

    return {
      label: item.label,
      component:
        item.label.toLowerCase() === "derivatives" ? (
          <DerivativesQuotesGrid quotes={busQuotes} {...others} />
        ) : item.label.toLowerCase() === "accumulators" ? (
          <AccumulatorQuotesGrid quotes={busQuotes} {...others} />
        ) : (
          <InsuranceQuotesGrid quotes={busQuotes} {...others} />
        ),
    };
  });

  return (
    <>
      {tabs.length > 1 && (
        <TabStrip
          selected={selected}
          onSelect={(e) => {
            const newIndex = e.selected;
            setSelected(newIndex);

            // update `tab` query param to reflect selected tab label (lowercase)
            const label = sortedListItems[newIndex]?.label?.toLowerCase();
            const newParams = new URLSearchParams(searchParams.toString());
            if (label) {
              newParams.set("tab", label);
            } else {
              newParams.delete("tab");
            }
            setSearchParams(newParams);
          }}
        >
          {tabs.map((item, i) => (
            <TabStripTab key={i} title={item.label}>
              {item.component}
            </TabStripTab>
          ))}
        </TabStrip>
      )}

      {tabs.length === 1 && <>{tabs[0].component}</>}
    </>
  );
};
